/**
 * One Manifest Review board: the same table, cells, docked record and scroll
 * rails as the Scheduling, Finance and UMED boards (the board kit), around
 * one tab's rows.
 *
 *   head    the tab's name, how many rows it holds (the rows below, never a
 *           different count), what it is for, its controls and its legend;
 *   table   DockTable: sticky header, frozen name column, a synced scroll
 *           rail on top and the frame's own bar below, a row click opens the
 *           record DOCKED beside the table (a full-height sheet under 900px);
 *   foot    the pager when the server pages, and the column key.
 *
 * Sorting here is of rows the page already holds (useClientSort) and only
 * where the data supports it. A server-paged list passes no sorts.
 */
import React, { useCallback, useId, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DockTable from './DockTable';
import type { BoardIdentity } from './DockTable';
import type { BoardDetailSection } from './DetailDock';
import { ColumnKey } from '../../../components/board/BoardLegend';
import type { BoardColumn } from '../../../components/board/types';
import '../../../components/board/board.css';
import './mfBoard.css';
import './mfBoardExtra.css';

export type { BoardColumn, BoardIdentity, BoardDetailSection };

type SortVal = string | number | null | undefined;

/** Sort rows the page holds. Nulls sort last either way; text compares without case. */
export function useClientSort<T>(rows: T[], sorters: Record<string, (r: T) => SortVal>, initial: { sort: string | null; dir?: 'asc' | 'desc' } = { sort: null }) {
  const [sort, setSort] = useState<string | null>(initial.sort);
  const [dir, setDir] = useState<'asc' | 'desc'>(initial.dir || 'asc');
  const sorts = useMemo(() => Object.keys(sorters), [sorters]);
  const sorted = useMemo(() => {
    const f = sort ? sorters[sort] : null;
    if (!f) return rows;
    const sign = dir === 'asc' ? 1 : -1;
    return rows
      .map((r, i) => ({ r, i, v: f(r) }))
      .sort((a, b) => {
        const an = a.v === null || a.v === undefined || a.v === '';
        const bn = b.v === null || b.v === undefined || b.v === '';
        if (an && bn) return a.i - b.i;
        if (an) return 1;
        if (bn) return -1;
        const c = typeof a.v === 'number' && typeof b.v === 'number'
          ? a.v - b.v
          : String(a.v).localeCompare(String(b.v), 'en-US', { sensitivity: 'base', numeric: true });
        return c === 0 ? a.i - b.i : c * sign;
      })
      .map((x) => x.r);
  }, [rows, sort, dir, sorters]);
  const onSort = useCallback((col: string, d: 'asc' | 'desc') => { setSort(col); setDir(d); }, []);
  return { rows: sorted, sort, dir, sorts, onSort };
}

/**
 * The open record lives in the URL (`param`), so a link or a refresh lands on it. Other params are kept.
 * `push`: opening and closing add a history entry, so Back walks between records (the Manifests tab's
 * ?review=, as goToReview always did). Otherwise the URL is replaced (an incidental change).
 */
export function useOpenParam(param = 'open', { push = false }: { push?: boolean } = {}): [string | null, (key: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const open = params.get(param);
  const set = useCallback((key: string | null) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key) next.set(param, key); else next.delete(param);
      return next;
    }, { replace: !push });
  }, [param, push, setParams]);
  return [open, set];
}

export interface MfBoardProps<T> {
  title: string;
  /** The number of rows this tab holds, from the same list the table draws. */
  count: number | null;
  units?: [string, string];
  what?: React.ReactNode;
  actions?: React.ReactNode;
  notice?: React.ReactNode;
  controls?: React.ReactNode;
  legend?: React.ReactNode;
  /** Shown in place of the table: loading, failed, nothing waiting, nothing matches. */
  state?: React.ReactNode;
  columns: BoardColumn<T>[];
  identity: BoardIdentity<T>;
  rows: T[];
  rowKey: (r: T) => string;
  openKey: string | null;
  onOpenChange: (key: string | null) => void;
  detailSections: (r: T) => BoardDetailSection[];
  detailLabel: (r: T) => string;
  sort?: string | null;
  dir?: 'asc' | 'desc';
  sorts?: string[];
  onSort?: (col: string, dir: 'asc' | 'desc') => void;
  pager?: React.ReactNode;
  foot?: React.ReactNode;
  notes?: string[];
  rowClass?: (r: T) => string;
}

export default function MfBoard<T>(p: MfBoardProps<T>) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const units = p.units || ['row', 'rows'];
  return (
    <section className="board mf-board" aria-labelledby={`${id}-t`}>
      <div className="board-head mf-board-head">
        <div className="mf-board-title">
          <h2 id={`${id}-t`}>{p.title}</h2>
          {p.count !== null && <span className="mf-board-count" data-count={p.count}>{p.count.toLocaleString('en-US')} {p.count === 1 ? units[0] : units[1]}</span>}
          {/* The count is the tab's rows; a search, a legend filter or a page narrows what is drawn, and says so. */}
          {p.count !== null && !p.state && p.rows.length !== p.count && <span className="mf-board-shown">{p.rows.length.toLocaleString('en-US')} shown here</span>}
          {p.actions && <span className="mf-board-actions">{p.actions}</span>}
        </div>
        {p.what && <p className="board-what">{p.what}</p>}
        {p.notice}
        {p.controls && <div className="board-controls mf-board-controls">{p.controls}</div>}
        {p.legend}
      </div>
      {p.state ? <div className="board-state">{p.state}</div> : (
        <DockTable<T>
          label={`${p.title} table`}
          columns={p.columns}
          rows={p.rows}
          rowKey={p.rowKey}
          identity={p.identity}
          openKey={p.openKey}
          onToggle={(k) => p.onOpenChange(k === p.openKey ? null : k)}
          onOpenChange={p.onOpenChange}
          detailSections={p.detailSections}
          detailLabel={p.detailLabel}
          memoryKey={`mf-${p.title}`}
          sort={p.sort ?? null}
          dir={p.dir || 'asc'}
          sorts={p.sorts || []}
          onSort={p.onSort || (() => undefined)}
          rowClass={p.rowClass}
        />
      )}
      {p.pager}
      <div className="board-foot">
        {p.foot}
        <ColumnKey columns={p.columns} identity={{ label: p.identity.label, help: p.identity.help }} notes={p.notes} />
      </div>
    </section>
  );
}
